/**
 * "Book a consultation" dialog.
 *
 * Mounted once in ElfShell and opened from any CTA via `useConsult()`. The
 * submission posts to /api/consultation, which stores the enquiry in Postgres.
 *
 * HubSpot: the CRM integration will read those stored rows and sync them, so
 * nothing here needs to change when it lands — capture first, sync after, and
 * a CRM outage can never lose a lead.
 *
 * A time can be chosen here too (ElfSlotPicker). The enquiry is always stored
 * first and the slot booked after: if HubSpot refuses the booking, the lead is
 * already safe and the visitor is told the time wasn't held.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import { apiHeaders, config } from '../../../auth/config';
import ElfSlotPicker, { type PickedSlot } from './ElfSlotPicker';
import { useConsult } from './elfConsult';

/** Keep in sync with CONSULT_INTERESTS in the API's consultation.types.ts. */
const INTERESTS = [
  'Marketing',
  'Web Development',
  'Cultural Intelligence',
  'Events & Festivals',
  'Merch & Commerce',
  'AI & GEO',
  'Sponsorship',
  'Something else',
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  interest: string;
  message: string;
}

const EMPTY: FormState = { name: '', email: '', phone: '', company: '', interest: '', message: '' };

export const ElfConsultModal: React.FC = () => {
  const consult = useConsult();
  const ctx = consult?.context ?? null;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  /** Chosen consultation time, if any, and what became of booking it. */
  const [slot, setSlot] = useState<PickedSlot | null>(null);
  const [booked, setBooked] = useState<string | null>(null);
  const [bookingFailed, setBookingFailed] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Reset each time it opens, seeding interest and email from the CTA.
  useEffect(() => {
    if (!ctx) return;
    setForm({ ...EMPTY, interest: ctx.interest ?? '', email: ctx.email ?? '' });
    setError(null);
    setDone(false);
    setSlot(null);
    setBooked(null);
    setBookingFailed(false);
    const id = window.setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [ctx]);

  // Escape closes; the page behind must not scroll while it's open.
  useEffect(() => {
    if (!ctx) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') consult?.close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [ctx, consult]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!ctx) return;
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(`${config.api.baseUrl}/api/consultation`, {
          method: 'POST',
          headers: apiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            ...form,
            // The chosen time rides along in the message as well, so the row
            // carries it even if the booking below never lands.
            message: slot ? `${form.message}\n\nRequested slot: ${slot.label}`.trim() : form.message,
            source: ctx.source,
            pageUrl: window.location.pathname,
          }),
        });
        if (!res.ok) {
          let msg = `Something went wrong (HTTP ${res.status})`;
          try {
            const body = await res.json();
            msg = body?.error || body?.message || msg;
          } catch {
            /* non-JSON error body — keep the status message */
          }
          throw new Error(msg);
        }
        // The lead is stored; now hold the time, if one was chosen.
        if (slot) {
          const [firstName, ...rest] = form.name.trim().split(/\s+/);
          const bookRes = await fetch(`${config.api.baseUrl}/api/scheduler/book`, {
            method: 'POST',
            headers: apiHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              startMillisUtc: slot.startMillisUtc,
              durationMillis: slot.endMillisUtc - slot.startMillisUtc,
              firstName: firstName || form.name,
              lastName: rest.join(' '),
              email: form.email,
              phone: form.phone,
            }),
          });
          if (bookRes.ok) setBooked(slot.label);
          else setBookingFailed(true);
        }
        setDone(true);
      } catch (err: any) {
        setError(err?.message ?? 'Could not send that — please try again.');
      } finally {
        setSaving(false);
      }
    },
    [ctx, form, slot],
  );

  if (!ctx) return null;

  const host = document.querySelector('.elf') ?? document.body;

  return createPortal(
    <div
      className="elf-consult"
      role="dialog"
      aria-modal="true"
      aria-label={ctx.title ?? 'Book a consultation'}
      onClick={(e) => {
        if (!cardRef.current?.contains(e.target as Node)) consult?.close();
      }}
    >
      <div className="elf-consult__card" ref={cardRef}>
        <button type="button" className="elf-consult__x" aria-label="Close" onClick={consult?.close}>
          <X size={16} />
        </button>

        {done ? (
          <div className="elf-consult__done">
            <span className="elf-consult__tick">
              <Check size={22} />
            </span>
            <h2 className="elf-consult__title">
              {booked ? 'You\u2019re booked in.' : 'You\u2019re on the list.'}
            </h2>
            <p className="wbody" style={{ fontSize: 15 }}>
              {booked ? (
                <>
                  Your consultation is confirmed for <b>{booked}</b> — the invite is on its way to{' '}
                  {form.email}. Thirty minutes, no deck required.
                </>
              ) : bookingFailed ? (
                <>
                  Thanks — we&rsquo;ve got your details. That time slipped away before we could hold
                  it, so someone will be in touch to fix another.
                </>
              ) : (
                <>
                  Thanks — we&rsquo;ve got your details and someone will be in touch shortly. Thirty
                  minutes, no deck required.
                </>
              )}
            </p>
            <button type="button" className="pillcta" onClick={consult?.close}>
              Close <span>→</span>
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="elf-consult__form">
            <span className="colhead">{ctx.source.replace(/\./g, ' · ')}</span>
            <h2 className="elf-consult__title">{ctx.title ?? 'Book a consultation'}</h2>
            <p className="wbody elf-consult__lede">
              Tell us who you are and what you&rsquo;re building. Thirty minutes, no deck required.
            </p>

            <div className="elf-consult__grid">
              <label className="elf-consult__field">
                <span className="colhead">Name *</span>
                <input
                  ref={firstFieldRef}
                  className="fld"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label className="elf-consult__field">
                <span className="colhead">Email *</span>
                <input
                  className="fld"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="you@company.com"
                />
              </label>
              <label className="elf-consult__field">
                <span className="colhead">Phone</span>
                <input
                  className="fld"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="Optional"
                />
              </label>
              <label className="elf-consult__field">
                <span className="colhead">Company</span>
                <input
                  className="fld"
                  value={form.company}
                  onChange={(e) => set('company', e.target.value)}
                  placeholder="Optional"
                />
              </label>
              <label className="elf-consult__field elf-consult__field--wide">
                <span className="colhead">What&rsquo;s it about</span>
                <select
                  className="fld"
                  value={form.interest}
                  onChange={(e) => set('interest', e.target.value)}
                >
                  <option value="">Choose one…</option>
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </label>
              <div className="elf-consult__field elf-consult__field--wide">
                <ElfSlotPicker value={slot} onChange={setSlot} />
              </div>
              <label className="elf-consult__field elf-consult__field--wide">
                <span className="colhead">Anything else</span>
                <textarea
                  className="fld"
                  rows={3}
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  placeholder="Timeline, budget, what you're releasing…"
                />
              </label>
            </div>

            {error && <span className="elf-consult__err">{error}</span>}

            <div className="elf-consult__actions">
              <button type="submit" className="pillcta mag" disabled={saving}>
                {saving ? <Loader2 size={14} className="elf-edit__spin" /> : null}
                {saving ? 'Sending…' : slot ? 'Book this time' : 'Request a call'}{' '}
                {!saving && <span>→</span>}
              </button>
              <button type="button" className="pillghost" onClick={consult?.close}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    host,
  );
};

export default ElfConsultModal;
